import { HighlightableButton, Image } from '@monofe/ui-common';
import { ImageLoadingStrategy } from '@monofe/ui-common/misc';
import { m } from 'framer-motion';
import type { ReactNode, UIEvent } from 'react';
import { memo, useCallback } from 'react';
import isEqual from 'react-fast-compare';
import ItemSubView from './ItemSubView';
import {
    StyleElement,
    animationStateForElement,
    animationVariantsForElement,
    classNameForElement,
    styleForElement,
} from './ItemView.style';
import type { IHamburgerItemViewProperties, IHamburgerSubItemViewProperties } from './interface';

function SubItemView(properties: {
    model: IHamburgerSubItemViewProperties;
    container: IHamburgerItemViewProperties;
}) {
    const style = useCallback(
        (element: StyleElement) => styleForElement(element, properties.container),
        [properties.container],
    );
    const className = useCallback(
        (element: StyleElement) => classNameForElement(element, properties.container),
        [properties.container],
    );

    return (
        <div className={className(StyleElement.SubItemCol)} style={style(StyleElement.SubItemCol)}>
            <ItemSubView {...properties.model} />
        </div>
    );
}

const MemoSubItemView = memo(SubItemView);

function TitleView(properties: IHamburgerItemViewProperties): JSX.Element {
    const style = useCallback(
        (element: StyleElement) => styleForElement(element, properties),
        [properties],
    );
    const className = useCallback(
        (element: StyleElement) => classNameForElement(element, properties),
        [properties],
    );

    return (
        <m.h2
            animate={animationStateForElement(StyleElement.ContainerTitle, properties)}
            variants={animationVariantsForElement(StyleElement.ContainerTitle)}
            className={className(StyleElement.ContainerTitle)}
            style={style(StyleElement.ContainerTitle)}
        >
            <span
                className={className(StyleElement.TitleText)}
                style={style(StyleElement.TitleText)}
            >
                {properties.title.text}
            </span>
        </m.h2>
    );
}

const MemoTitleView = memo(TitleView);

function useOnClick(properties: IHamburgerItemViewProperties) {
    return useCallback(
        (event: UIEvent) => {
            if (properties.isShowingSubItems) {
                return;
            }

            event.stopPropagation();
            properties.saveCurrentScrollPosition?.();
            properties.onClick?.(properties);
        },
        [properties],
    );
}

function ContainerView(
    properties: IHamburgerItemViewProperties & { children?: ReactNode },
): JSX.Element {
    const style = useCallback(
        (element: StyleElement) => styleForElement(element, properties),
        [properties],
    );
    const className = useCallback(
        (element: StyleElement) => classNameForElement(element, properties),
        [properties],
    );

    const onClick = useOnClick(properties);

    return (
        <HighlightableButton
            onClick={onClick}
            href={properties.link}
            isLink={Boolean(properties.link)}
            animate={animationStateForElement(StyleElement.Container, properties)}
            variants={animationVariantsForElement(StyleElement.Container)}
            highlightable={!properties.isShowingSubItems}
            className={className(StyleElement.Container)}
            style={style(StyleElement.Container)}
        >
            {properties.children}
        </HighlightableButton>
    );
}

const MemoContainerView = memo(ContainerView);

function View(properties: IHamburgerItemViewProperties): JSX.Element {
    const style = useCallback(
        (element: StyleElement) => styleForElement(element, properties),
        [properties],
    );
    const className = useCallback(
        (element: StyleElement) => classNameForElement(element, properties),
        [properties],
    );

    return (
        <MemoContainerView {...properties}>
            <Image
                urls={properties.backgroundImage.urls}
                mode={properties.backgroundImage.mode}
                className={className(StyleElement.BackgroundImage)}
                style={style(StyleElement.BackgroundImage)}
                loading={
                    properties.shouldLazyLoad
                        ? ImageLoadingStrategy.Lazy
                        : ImageLoadingStrategy.Eager
                }
            />
            <m.section
                animate={animationStateForElement(StyleElement.ContainerBottom, properties)}
                variants={animationVariantsForElement(StyleElement.ContainerBottom)}
                className={className(StyleElement.ContainerBottom)}
                style={style(StyleElement.ContainerBottom)}
            >
                <MemoTitleView {...properties} />
                {properties.subItems.length > 0 && properties.isShowingSubItems && (
                    <m.nav
                        animate={animationStateForElement(
                            StyleElement.SubItemContainer,
                            properties,
                        )}
                        variants={animationVariantsForElement(StyleElement.SubItemContainer)}
                        className={className(StyleElement.SubItemContainer)}
                        style={style(StyleElement.SubItemContainer)}
                    >
                        {properties.subItems.map((model, index) => (
                            <MemoSubItemView key={index} model={model} container={properties} />
                        ))}
                    </m.nav>
                )}
            </m.section>
        </MemoContainerView>
    );
}

function _comparable(
    objectIn: IHamburgerItemViewProperties,
): Partial<IHamburgerItemViewProperties> {
    const objectOut: Partial<IHamburgerItemViewProperties> = { ...objectIn };
    delete objectOut.data;
    delete objectOut.subItems;
    delete objectOut.onShow;
    return objectOut;
}

const propertiesEqual = (
    previous: IHamburgerItemViewProperties,
    next: IHamburgerItemViewProperties,
): boolean => {
    const previousCompare = _comparable(previous);
    const nextCompare = _comparable(next);
    const match = isEqual(previousCompare, nextCompare);
    return match;
};

export default memo(View, propertiesEqual);
